import React, { useEffect, useMemo, useState } from "react";
import BsModal from "../Common/BsModal";
import { useCurrency } from "../../util/SystemVariables";
import { formatDate, numberFormat } from "../../util/araknerdlib";
import CallOut from "../Common/CallOut";
import { useTheme } from "../../DataStore/ThemeContext";

const badgeTone = (s) =>
  s === "Approved" ? "success" : s === "Rejected" ? "danger" : "warning";

const StatTile = ({ label, value, big = false }) => (
  <div
    className="p-3 rounded"
    style={{
      background: "#fff",
      border: "1px solid rgba(0,0,0,.08)",
      height: "100%",
    }}
  >
    <div className="tx-11 text-muted">{label}</div>
    <div className={big ? "tx-18 font-weight-bold" : "tx-13 font-weight-semibold"}>
      {value ?? "—"}
    </div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="d-flex" style={{ gap: 10 }}>
    <div className="tx-11 text-muted" style={{ minWidth: 120 }}>
      {k}
    </div>
    <div className="tx-13 font-weight-semibold">{v ?? "—"}</div>
  </div>
);

const ApprovalModal = ({
  show,
  onClose,
  record,
  handleApprove,
  handleReject,
}) => {
  const currency = useCurrency();
  const [comment, setComment] = useState("");
  const [loadingAction, setLoadingAction] = useState(null); // "approve" | "reject"
  const {currentNotify} = useTheme();

  useEffect(() => {
    setComment("");
    setLoadingAction(null);
  }, [record?.id, show]);


  const isPending = record?.approval_status === "Pending";
  const requestedBy =
    record?.requested_by?.full_name || record?.requested_by?.username || "—";

  const payload = record?.payload || {};
  const paymentDetails = Array.isArray(payload.payment_details)
    ? payload.payment_details
    : [];

  const payloadPairs = useMemo(
    () => [
      ["Payment Account", payload.payment_account],
      ["Txn Ref", payload.transaction_ref],
      ["Txn Date", payload.transaction_date],
      ["Deferred Income", payload.deferred_income ? "Yes" : "No"],
    ],
    [payload]
  );

    if (!record) return null;


  const onApprove = async () => {
    if (!isPending || loadingAction) return;
    setLoadingAction("approve");
    try {
      await handleApprove(record.id, comment);
    } finally {
      setLoadingAction(null);
    }
  };

  const onReject = async () => {
    if (!isPending || loadingAction) return;
    setLoadingAction("reject");
    try {
      await handleReject(record.id, comment);
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <BsModal
      show={show}
      onClose={onClose}
      title="Review & Approve"
      size="modal-lg"
      footer={
        <button
          type="button"
          className="btn btn-light"
          onClick={onClose}
          disabled={!!loadingAction}
        >
          Cancel
        </button>
      }
    >
      <CallOut
        type="info"
        title="Approval rules"
        description='Only “Pending” can be acted on. Once approved/rejected, lock edits and keep audit trail (requested_by, approved_by, approval_date, comments).'
      />

      {/* Centered container (prevents "squeezed left") */}
      <div className="mt-3" style={{ maxWidth: 920, margin: "0 auto" }}>
        {/* Header */}
        <div
          className="p-3 rounded"
        >
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <div className="tx-16 font-weight-bold">{record.reference_no}</div>
              <div className="tx-12 text-muted">{record.module_name}</div>
            </div>

            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <span className="tx-12 text-muted">Status</span>
              <span
                className={`badge badge-${badgeTone(record.approval_status)} tx-12`}
              >
                {record.approval_status}
              </span>
            </div>
          </div>

          {/* Summary tiles */}
          <div className="row mt-3">
            <div className="col-md-4 mb-2 mb-md-0">
              <StatTile
                label="Amount"
                value={`${currency} ${numberFormat(record.amount)}`}
                big
              />
            </div>
            <div className="col-md-4 mb-2 mb-md-0">
              <StatTile label="Requested by" value={requestedBy} />
            </div>
            <div className="col-md-4">
              <StatTile
                label="Dates"
                value={
                  <div>
                    <div className="tx-12">
                      <span className="text-muted">Txn:</span>{" "}
                      <span className="font-weight-semibold">
                        {formatDate(record.transaction_date)}
                      </span>
                    </div>
                    <div className="tx-12 mt-1">
                      <span className="text-muted">Created:</span>{" "}
                      <span className="font-weight-semibold">
                        {formatDate(record.created_at)}
                      </span>
                    </div>
                  </div>
                }
              />
            </div>
          </div>

          {/* Narration */}
          <div className="mt-3">
            <div className="tx-11 text-muted mb-1">Narration</div>
            <div className="tx-14 font-weight-semibold">
              {record.description || "—"}
            </div>

            {record.comments ? (
              <div
                className="mt-2 p-2 rounded"
                style={{
                  background: "rgba(255,193,7,.12)",
                  border: "1px solid rgba(255,193,7,.25)",
                }}
              >
                <div className="tx-12 font-weight-semibold mb-1">
                  Requester note
                </div>
                <div className="tx-13">{record.comments}</div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Payload */}
        <div className="mt-3 p-3 rounded" style={{ border: "1px solid rgba(0,0,0,.08)" }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="tx-13 font-weight-bold">Module payload</div>
            <div className="tx-12 text-muted">{currency}</div>
          </div>

          <div className="row">
            {payloadPairs.map(([k, v]) => (
              <div className="col-md-6 mb-2" key={k}>
                <KV k={k} v={v ?? "—"} />
              </div>
            ))}
          </div>
        </div>

        {/* Payment details */}
        <div className="mt-3">
          <div className="tx-13 font-weight-bold mb-2">Payment details</div>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead style={{ background: "rgba(0,0,0,.02)" }}>
                <tr className="tx-12 text-muted">
                  <th style={{ width: 55 }}>#</th>
                  <th style={{ width: 140 }}>Fee ID</th>
                  <th className="text-right" style={{ width: 180 }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {paymentDetails.map((p, idx) => (
                  <tr key={idx}>
                    <td className="tx-12">{idx + 1}</td>
                    <td className="tx-13 font-weight-semibold">{p.fee_id}</td>
                    <td className="tx-13 text-right">
                      {currency} {numberFormat(p.amount)}
                    </td>
                  </tr>
                ))}
                {!paymentDetails.length && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-3">
                      No payment lines found in payload.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <details className="mt-2">
            <summary className="tx-12 text-muted" style={{ cursor: "pointer" }}>
              View raw payload
            </summary>
            <pre
              className="mt-2 mb-0 p-2"
              style={{
                background: "#0b1020",
                color: "#e5e7eb",
                borderRadius: 10,
                fontSize: 12,
                overflowX: "auto",
              }}
            >
              {JSON.stringify(payload, null, 2)}
            </pre>
          </details>
        </div>

        <div className="mt-3">{currentNotify}</div>
        {/* Actions */}
        <div className="mt-4 p-3 rounded" style={{ border: "1px solid rgba(0,0,0,.08)" }}>
          <div className="tx-13 font-weight-bold mb-2">Approval action</div>

          <label className="tx-12 text-muted mb-1">Approver comment (optional)</label>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Add an approval/rejection note..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={!isPending || !!loadingAction}
          />

          <div className="d-flex flex-wrap mt-3" style={{ gap: 10 }}>
            <button
              type="button"
              className="btn ripple btn-success"
              onClick={onApprove}
              disabled={!isPending || !!loadingAction}
              style={{ minWidth: 200 }}
            >
              {loadingAction === "approve" ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Approving...
                </>
              ) : (
                <>
                  <i className="fa fa-check mr-2" />
                  Approve &amp; Post
                </>
              )}
            </button>

            <button
              type="button"
              className="btn ripple btn-danger"
              onClick={onReject}
              disabled={!isPending || !!loadingAction}
              style={{ minWidth: 140 }}
            >
              {loadingAction === "reject" ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Rejecting...
                </>
              ) : (
                <>
                  <i className="fa fa-times mr-2" />
                  Reject
                </>
              )}
            </button>

            {!isPending && (
              <div className="ml-auto tx-12 text-muted align-self-center">
                Locked (only Pending can be acted on)
              </div>
            )}
          </div>

          <div className="tx-12 text-muted mt-2">
            Approve = marks record as Approved and posts payload into ledgers (one DB transaction).
          </div>
        </div>
      </div>
    </BsModal>
  );
};

export default ApprovalModal;
